import axios from 'axios';
import dayjs from 'dayjs';
import {random} from 'lodash-es';
import * as lodash from 'lodash-es';
import queryString from 'query-string';
import classnames from 'classnames';

import sdk from '@/sdk';
import {feLogger} from '@/buried';
import {ServerConfig} from '@/config/routers';
import * as store from '@/store';
import {WizKnownError} from '@/sdk/common/error';
import {fetchDocToken} from '@/services';
import {WizRequestOptions} from '@/interface';
import hotKey from '@/hotkeys/index-new';
import {isInKuShare} from '@/utils/urlUtils';

import {IRegisterOptions, IUtils, RepType} from '../types';

export const getFrontTraceId = () => {
    const timestamp = +new Date();
    const randomNumber = random(1000, 9999);
    const buildId = process.env.REACT_APP_BUILD_ID;
    const userGuid = sdk.as.user?.userGuid || '';
    const traceId = `${timestamp}_${randomNumber}_${buildId}_${userGuid}`;

    return traceId;
};


const addFrontTraceInfo = (opt: WizRequestOptions) => {
    const traceId = getFrontTraceId();

    const url = queryString.stringifyUrl({
        url: opt.url || '',
        query: {
            'front_traceId': traceId,
        },
    });

    return {
        ...opt,
        url,
        headers: {
            ...opt.headers,
            'front-traceId': traceId,
        },
    };
};

const formatRteRequestOptions = (options: WizRequestOptions): WizRequestOptions => {
    const {accessToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN) || {};
    const docGuid = store.getData(store.KEYS.CUR_DOCUMENT)?.docGuid || '';

    // 处理 URL，传相对路径时，拼接成完整 URL
    let url = options.url || '';
    if (!url.startsWith('http')) {
        url = new URL(url, ServerConfig.rte).toString();
    }

    // 合并 headers
    const defaultHeaders = {
        Authorization: accessToken,
        'x-wiki-doc': docGuid,
    };
    const mergedHeaders = {
        ...defaultHeaders,
        ...(options.headers || {}),
    };

    return addFrontTraceInfo({
        ...options,
        url,
        headers: mergedHeaders,
    });
};

const api = axios.create({
    timeout: 15 * 1000, // 请求超时时间
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// 请求拦截器
// 预留设计，可扩展逻辑
api.interceptors.request.use(
    config => {
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// 响应拦截器
// 预留设计，可扩展逻辑
api.interceptors.response.use(
    response => {
        return response.data;
    },
    error => {
        return Promise.reject(error);
    }
);

export const utils: IUtils = {
    request: (options) => {
        return new Promise<any>((resolve, reject) => {
            sdk.serverBase.request(options).then(resolve).catch(reject);
        });
    },
    axios: (options) => {
        return new Promise<any>((resolve, reject) => {
            api(options).then(resolve).catch(reject);
        });
    },
    ksRequest: async (options) => {
        try {
            const ksServer = await sdk.getKsServer();
            return ksServer.request({
                wikiAccessToken: ksServer.wikiAccessToken,
                ...options,
            });
        } catch (e) {
            console.error(e);
        }
    },
    asRequest: async (options) => {
        try {
            return sdk.as.request({
                wikiToken: sdk.user?.token,
                ...options,
            });
        } catch (e) {
            console.error(e);
        }
    },
    rteRequest: async (options) => {
        try {
            return await sdk.serverBase.request(
                formatRteRequestOptions(options)
            );
        } catch (e) {
            if (e instanceof WizKnownError) {
                const {returnCode} = e.toResult();

                if (returnCode === 402) {
                    const repository = store.getCurRepository();
                    const curDoc = store.getCurDocument();

                    if (repository && curDoc) {
                        await fetchDocToken(repository, curDoc.docGuid);

                        return sdk.serverBase.request(
                            formatRteRequestOptions(options)
                        );
                    }
                }
            }
            console.error(e);
        }
    },
    kuLogger: (options) => {
        const {repId, params, type = RepType.Click} = options;

        try {
            const data: Record<string, any> = {
                rep_id: repId,
                rep_type: type,
            };

            if (params) {
                data.rep_params = params;
            }
            feLogger(data);
        } catch (e) {
            console.error(e);
        }
    },
    dayjs,
    getToken: async (ak) => {
        try {
            const ugateToken = await sdk.as.request({
                method: 'get',
                url: `${ServerConfig.ku}/ugate/v2/token`,
                params: {
                    ak,
                },
                ignoreUgateTokenInvalid: true,
            });

            if (!ugateToken) {
                throw Error('ugateToken 获取失败');
            }
            return ugateToken;
        } catch (e) {
            console.error(e);
        }
    },
    getServerConfig: () => ServerConfig,
    registerKuHotKey: (params: IRegisterOptions) => {
        const {hotkey, callback, options = {}} = params;
        try {
            hotKey.register(hotkey, callback, options);
        } catch (e) {
            console.error(e);
        }
    },
    unregisterKuHotKey: (hotkey: string) => {
        hotKey.unregister(hotkey);
    },
    getRegisteredHotkeys: () => {
        return hotKey.getRegisteredHotkeys();
    },
    hotkeyTrim: (hotkey: string) => {
        return hotKey.hotkeyTrim(hotkey);
    },
    isInKuShare: () => {
        return Boolean(isInKuShare());
    },
    classnames: (...args: any[]) => {
        return classnames(...args);
    },
    lodash: () => lodash,
};
